import { useState, useEffect } from "react";
import {
  X,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Layers,
  FileText,
  BarChart2,
  Loader2,
  CheckCircle2,
  ExternalLink,
  ArrowLeft,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, ApiError } from "../../lib/api";
import { ONLINE_READING_MESSAGE, CONTENT_NOT_AVAILABLE_MESSAGE } from "../../lib/constants";
import { formatDisplayTitle } from "../../lib/formatTitle";
import { GeneratedContent, ContentFigure } from "../GeneratedContent";
import { MathText } from "../MathText";
import { Modal } from "../ui/Modal";

interface StudentChapterReaderModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: string;
  moduleTitle?: string;
  initialChapterId?: string;
  inline?: boolean;
}

interface ChapterSummary {
  id: string;
  title: string;
  sortOrder?: number;
  startPage?: number | null;
  endPage?: number | null;
  topics?: Array<{ id: string; title: string; sortOrder?: number }>;
}

interface ChapterContentResponse {
  chapterId: string;
  chapterTitle: string;
  topicId: string | null;
  topicTitle: string;
  markdown: string;
  figures: ContentFigure[];
  version: number;
  source: string;
  pages: number[];
  topics: Array<{ id: string; title: string; sortOrder?: number }>;
  topicContents: Array<{
    id: string;
    topicId: string | null;
    topicTitle: string;
    markdown: string;
    figures: ContentFigure[];
    version: number;
    source: string;
    pages: number[];
  }>;
}

export function StudentChapterReaderModal({
  isOpen,
  onClose,
  moduleId,
  moduleTitle,
  initialChapterId,
  inline = false,
}: StudentChapterReaderModalProps) {
  const { tokens } = useAuth();
  const token = tokens?.accessToken ?? null;

  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const [selectedChapterId, setSelectedChapterId] = useState<string>("");
  const [selectedTopicId, setSelectedTopicId] = useState<string>("");

  const [loadingCurriculum, setLoadingCurriculum] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);

  const [chapterContent, setChapterContent] = useState<ChapterContentResponse | null>(null);

  const [fontSize, setFontSize] = useState<"sm" | "base" | "lg">(() => {
    try {
      const stored = localStorage.getItem("soorgaa_reader_font_size");
      if (stored === "sm" || stored === "base" || stored === "lg") return stored;
    } catch {}
    return "base";
  });

  const changeFontSize = (nextSize: "sm" | "base" | "lg") => {
    setFontSize(nextSize);
    try {
      localStorage.setItem("soorgaa_reader_font_size", nextSize);
    } catch {}
  };

  // Remember last read chapter per module
  useEffect(() => {
    if (moduleId && selectedChapterId) {
      try {
        localStorage.setItem(`soorgaa_last_chapter_${moduleId}`, selectedChapterId);
      } catch {}
    }
  }, [moduleId, selectedChapterId]);

  // Load curriculum (list of chapters and topics) when modal opens
  useEffect(() => {
    if (!isOpen || !token || !moduleId) {
      setChapters([]);
      setSelectedChapterId("");
      setSelectedTopicId("");
      setChapterContent(null);
      setContentError(null);
      return;
    }

    setLoadingCurriculum(true);
    setContentError(null);
    api.student.modules
      .curriculum(token, moduleId)
      .then((data) => {
        const chList = data.chapters || [];
        setChapters(chList);
        if (chList.length > 0) {
          let savedCh: string | null = null;
          try {
            savedCh = localStorage.getItem(`soorgaa_last_chapter_${moduleId}`);
          } catch {}
          const defaultCh = initialChapterId && chList.some((c) => c.id === initialChapterId)
            ? initialChapterId
            : (savedCh && chList.some((c) => c.id === savedCh))
            ? savedCh
            : chList[0].id;
          setSelectedChapterId(defaultCh);
        } else {
          setContentError(CONTENT_NOT_AVAILABLE_MESSAGE);
        }
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) {
          setContentError(CONTENT_NOT_AVAILABLE_MESSAGE);
        } else {
          setContentError(err instanceof ApiError ? err.message : "Failed to load chapters");
        }
      })
      .finally(() => {
        setLoadingCurriculum(false);
      });
  }, [isOpen, token, moduleId, initialChapterId]);

  // Load chapter content when selected chapter changes
  useEffect(() => {
    if (!isOpen || !token || !moduleId || !selectedChapterId) return;

    setLoadingContent(true);
    setContentError(null);
    api.student.modules
      .chapterContent(token, moduleId, selectedChapterId, selectedTopicId || undefined)
      .then((data) => {
        setChapterContent(data);
        if (!selectedTopicId && data.topicId) {
          setSelectedTopicId(data.topicId);
        }
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 404) {
          setContentError(CONTENT_NOT_AVAILABLE_MESSAGE);
        } else {
          setContentError(err instanceof ApiError ? err.message : "Failed to load chapter content");
        }
        setChapterContent(null);
      })
      .finally(() => {
        setLoadingContent(false);
      });
  }, [isOpen, token, moduleId, selectedChapterId, selectedTopicId]);

  if (!isOpen) return null;

  const currentChapter = chapters.find((c) => c.id === selectedChapterId);
  const availableTopics = chapterContent?.topics || currentChapter?.topics || [];

  // Active topic index for next/prev navigation
  const currentTopicIndex = availableTopics.findIndex(
    (t) => t.id === (chapterContent?.topicId || selectedTopicId)
  );

  const handleNextTopic = () => {
    if (currentTopicIndex >= 0 && currentTopicIndex < availableTopics.length - 1) {
      setSelectedTopicId(availableTopics[currentTopicIndex + 1].id);
    }
  };

  const handlePrevTopic = () => {
    if (currentTopicIndex > 0) {
      setSelectedTopicId(availableTopics[currentTopicIndex - 1].id);
    }
  };

  const modalContent = (
    <div className={`relative w-full ${inline ? "rounded-lg bg-card border border-border shadow-xs" : "max-w-5xl max-h-[92vh] rounded-xl bg-card border border-border overflow-hidden"} flex flex-col`}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-card/60 backdrop-blur">
        <div className="flex items-center gap-3 min-w-0">
          {inline && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted text-foreground transition shrink-0 mr-1"
            >
              <ArrowLeft size={14} /> Back to modules
            </button>
          )}
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <BookOpen size={20} />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold truncate text-foreground">
              {currentChapter ? currentChapter.title : (moduleTitle ? formatDisplayTitle(moduleTitle) : "Chapter reader")}
            </h2>
            <p className="text-xs text-muted-foreground truncate">
              {moduleTitle ? `${formatDisplayTitle(moduleTitle)} • ` : ""}
              {chapterContent?.pages?.length
                ? `Pages ${Math.min(...chapterContent.pages)}–${Math.max(...chapterContent.pages)}`
                : "Stored chapter content"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Font Size Controls */}
          <div className="flex items-center gap-1 bg-secondary/80 rounded-md p-0.5 border border-border">
            <button
              type="button"
              onClick={() => changeFontSize(fontSize === "lg" ? "base" : "sm")}
              disabled={fontSize === "sm"}
              className="px-2 py-0.5 rounded text-xs font-semibold hover:bg-card disabled:opacity-40 transition cursor-pointer"
              aria-label="Decrease font size"
            >
              A−
            </button>
            <span className="text-xs text-muted-foreground px-1 font-mono uppercase">{fontSize}</span>
            <button
              type="button"
              onClick={() => changeFontSize(fontSize === "sm" ? "base" : "lg")}
              disabled={fontSize === "lg"}
              className="px-2 py-0.5 rounded text-xs font-semibold hover:bg-card disabled:opacity-40 transition cursor-pointer"
              aria-label="Increase font size"
            >
              A+
            </button>
          </div>

          {/* Chapter Selector Dropdown */}
          {chapters.length > 1 && (
            <select
              value={selectedChapterId}
              onChange={(e) => {
                setSelectedChapterId(e.target.value);
                setSelectedTopicId("");
              }}
              className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium bg-background text-foreground outline-none focus:border-primary"
            >
              {chapters.map((ch, idx) => (
                <option key={ch.id} value={ch.id}>
                  Chapter {idx + 1}: {ch.title}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition cursor-pointer"
            aria-label="Close reader"
          >
            <X size={18} />
          </button>
        </div>
        </div>

        {/* Online Reading Status Banner */}
        <div className="flex items-center gap-2 px-6 py-2 bg-warning/10 border-b border-warning/30 text-warning text-xs font-medium">
          <span className="inline-block w-2 h-2 rounded-full bg-warning animate-pulse shrink-0" />
          <span>Online reading is coming soon.</span>
        </div>

        {/* Topic navigation tabs */}
        {availableTopics.length > 0 && (
          <div className="flex items-center gap-1.5 px-6 py-2.5 border-b border-border bg-muted/30 overflow-x-auto scrollbar-none">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider shrink-0 mr-1 flex items-center gap-1">
              <Layers size={13} /> Topics
            </span>
            {availableTopics.map((top) => {
              const active = (chapterContent?.topicId || selectedTopicId) === top.id;
              return (
                <button
                  key={top.id}
                  onClick={() => setSelectedTopicId(top.id)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition ${active
                    ? "bg-primary text-primary-foreground"
                    : "bg-background text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                >
                  {top.title}
                </button>
              );
            })}
          </div>
        )}

        {/* Content Body */}
        <div className={`flex-1 ${inline ? "" : "overflow-y-auto"} px-6 py-6 space-y-6`}>
          {loadingCurriculum || loadingContent ? (
            <div className="py-24 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="animate-spin text-primary" size={32} />
              <p className="text-sm font-medium">Loading chapter…</p>
            </div>
          ) : contentError ? (
            <div className="py-16 text-center max-w-md mx-auto space-y-3">
              <div className="w-10 h-10 rounded-xs bg-primary/10 text-primary flex items-center justify-center mx-auto">
                <FileText size={20} />
              </div>
              <h3 className="text-sm font-semibold text-foreground">Chapter reader</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{contentError}</p>
            </div>
          ) : chapterContent ? (
            <div className="max-w-3xl mx-auto space-y-6">
              {/* Distinct Topic Title Banner */}
              <div className="bg-primary/10 border border-primary/20 rounded-xs p-4">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-1">
                  <span>Topic</span>
                  {chapterContent.pages?.length > 0 && (
                    <span>• Pages {Math.min(...chapterContent.pages)}–{Math.max(...chapterContent.pages)}</span>
                  )}
                  {chapterContent.figures?.length > 0 && (
                    <span>• {chapterContent.figures.length} figure{chapterContent.figures.length > 1 ? "s" : ""}</span>
                  )}
                </div>
                <h1 className="text-2xl font-bold tracking-tight text-foreground">
                  <MathText inline>{chapterContent.topicTitle || currentChapter?.title || "Overview"}</MathText>
                </h1>
              </div>

              {/* Stored Teaching Explanation with Markdown + LaTeX */}
              <div className={`prose prose-neutral dark:prose-invert max-w-none ${
                fontSize === "sm" ? "text-sm leading-relaxed" : fontSize === "lg" ? "text-lg leading-[1.8]" : "text-base leading-[1.7]"
              }`}>
                <GeneratedContent figures={chapterContent.figures}>
                  {chapterContent.markdown}
                </GeneratedContent>
              </div>

              {/* Dedicated Figure Cards if present */}
              {chapterContent.figures && chapterContent.figures.length > 0 && (
                <div className="pt-6 border-t border-border space-y-3">
                  <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                    <BarChart2 size={16} className="text-primary" />
                    Figures and diagrams ({chapterContent.figures.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {chapterContent.figures.map((fig) => (
                      <div
                        key={fig.id}
                        className="p-3.5 rounded-xl border border-border bg-muted/40 space-y-1.5 text-xs"
                      >
                        <div className="flex items-center justify-between text-muted-foreground font-semibold">
                          <span className="font-mono text-primary text-xs">{fig.id}</span>
                          {fig.page && <span>Page {fig.page}</span>}
                        </div>
                        {fig.caption && (
                          <p className="font-medium text-foreground">{fig.caption}</p>
                        )}
                        {fig.description && (
                          <div className="text-muted-foreground text-xs leading-relaxed">
                            <GeneratedContent inline>{fig.description}</GeneratedContent>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer with Prev / Next Topic navigation */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-border bg-card/60 backdrop-blur text-xs">
          <button
            onClick={handlePrevTopic}
            disabled={currentTopicIndex <= 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border font-medium text-foreground disabled:opacity-40 disabled:pointer-events-none hover:bg-muted transition"
          >
            <ChevronLeft size={14} /> Previous topic
          </button>
          <span className="text-muted-foreground">
            {availableTopics.length > 0
              ? `Topic ${Math.max(1, currentTopicIndex + 1)} of ${availableTopics.length}`
              : ""}
          </span>
          <button
            onClick={handleNextTopic}
            disabled={currentTopicIndex >= availableTopics.length - 1}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border font-medium text-foreground disabled:opacity-40 disabled:pointer-events-none hover:bg-muted transition"
          >
            Next topic <ChevronRight size={14} />
          </button>
        </div>
      </div>
    );

  if (inline) {
    return modalContent;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      showCloseButton={false}
      className="p-0 overflow-hidden max-w-5xl"
    >
      {modalContent}
    </Modal>
  );
}
